import { api } from '../../../api/client';
import type {
  RegisterResult, RegisterSuboutRequest, SuboutLookups, SuboutNotificationOutcome, SuboutScanResult,
  SuboutRegistrationKey, SuboutRegistrationRow, SuboutSampleDetailRow,
  SuboutTestOrderActionResult, SuboutTestOrderDetail, SuboutTestPriceComputeRequest,
  SuboutTestPriceResult, UpdateTestOrderRequest,
} from './types';

// Sample SubContract Tracking → Subout Registration — thin axios wrappers over
// /api/v1/subout-registration. The shared client injects the JWT + loader.

const BASE = '/api/v1/subout-registration';

/** The registration queue for a job Received-date window (yyyy-MM-dd, inclusive;
 * an omitted bound is open-ended). `silent` suppresses the global loader for the
 * background option fetch. */
export async function fetchSuboutQueue(
  from?: string | null, to?: string | null, silent = false,
): Promise<SuboutRegistrationRow[]> {
  const res = await api.get(BASE + '/queue', {
    params: { from: from || undefined, to: to || undefined },
    ...(silent ? { silentLoading: true } : {}),
  });
  return (res.data as { data: SuboutRegistrationRow[] }).data ?? [];
}

/** Contract labs + TAT + preservatives + delivery services for the dialog. */
export async function fetchSuboutLookups(): Promise<SuboutLookups> {
  const res = await api.get(BASE + '/lookups');
  return (res.data as { data: SuboutLookups }).data;
}

/** The selected pending samples for the detail view's "Subout Samples" grid. */
export async function fetchSampleDetails(keys: SuboutRegistrationKey[]): Promise<SuboutSampleDetailRow[]> {
  const res = await api.post(BASE + '/sample-details', keys);
  return (res.data as { data: SuboutSampleDetailRow[] }).data ?? [];
}

/** Scan Code: the grid rows that carry the scanned container label. A refusal
  * (bad format / not on the screen / not Subout) rejects with G5's message. */
export async function scanContainer(label: string, keys: SuboutRegistrationKey[]): Promise<SuboutScanResult> {
  const res = await api.post(BASE + '/scan', { label, keys }, { silentLoading: true } as never);
  return (res.data as { data: SuboutScanResult }).data;
}

/** Register the selected pending tests into one new subout order. */
export async function registerSubout(request: RegisterSuboutRequest): Promise<RegisterResult> {
  const res = await api.post(BASE + '/register', request);
  return (res.data as { data: RegisterResult }).data;
}

// ── Subout Test Order — Edit / Delete / Submit ──────────────────────────────
const TO = '/api/v1/subout-test-order';

/** One order's header for the Edit detail view. */
export async function fetchTestOrder(oid: string): Promise<SuboutTestOrderDetail> {
  const res = await api.get(`${TO}/order/${oid}`);
  return (res.data as { data: SuboutTestOrderDetail }).data;
}

/** An order's linked samples (the Subout Samples grid). */
export async function fetchTestOrderSamples(oid: string): Promise<SuboutSampleDetailRow[]> {
  const res = await api.get(`${TO}/order/${oid}/samples`);
  return (res.data as { data: SuboutSampleDetailRow[] }).data ?? [];
}

/** Edit → Save: update the order's header; returns the refreshed order. */
export async function updateTestOrder(oid: string, req: UpdateTestOrderRequest): Promise<SuboutTestOrderDetail> {
  const res = await api.put(`${TO}/order/${oid}`, req);
  return (res.data as { data: SuboutTestOrderDetail }).data;
}

/** Submit the selected orders (bulk); each submitted order's contract lab is emailed. */
export async function submitTestOrders(orderOids: string[]): Promise<{
  submitted: number; rows: SuboutTestOrderActionResult[]; emails: SuboutNotificationOutcome[];
}> {
  const res = await api.post(`${TO}/submit`, orderOids);
  return (res.data as { data: { submitted: number; rows: SuboutTestOrderActionResult[]; emails: SuboutNotificationOutcome[] } }).data;
}

/** Subout Samples → Add picker: every pending subout test. */
export async function fetchTestOrderCandidates(oid: string): Promise<SuboutSampleDetailRow[]> {
  const res = await api.get(`${TO}/order/${oid}/candidates`);
  return (res.data as { data: SuboutSampleDetailRow[] }).data ?? [];
}

/** The order's Test Price rows, synced with its samples. */
export async function fetchTestPrices(oid: string): Promise<SuboutTestPriceResult> {
  const res = await api.get(`${TO}/order/${oid}/test-prices`);
  return (res.data as { data: SuboutTestPriceResult }).data;
}

/** Recompute the Test Price rows after one change (server rules; nothing is saved). */
export async function computeTestPrices(req: SuboutTestPriceComputeRequest): Promise<SuboutTestPriceResult> {
  const res = await api.post(`${TO}/test-prices/compute`, req, { silentLoading: true } as never);
  return (res.data as { data: SuboutTestPriceResult }).data;
}

/** The Due Date for a TAT, counted from `from` (the order's received date; now when new). */
export async function fetchDueDate(tatOid: string, from?: string | null): Promise<string | null> {
  const res = await api.get(BASE + '/due-date', {
    params: { tat: tatOid, from: from || undefined }, silentLoading: true,
  } as never);
  return (res.data as { data: { dueDate: string | null } }).data?.dueDate ?? null;
}

/** Notification Queue → Resend the contract-lab email. */
export async function resendNotification(oid: string): Promise<SuboutNotificationOutcome> {
  const res = await api.post(`/api/v1/subout-notifications/${oid}/resend`);
  return (res.data as { data: SuboutNotificationOutcome }).data;
}

/** Notification Queue → EDD Template: the order's EDD workbook (download). */
export async function downloadEddTemplate(oid: string): Promise<{ blob: Blob; fileName: string }> {
  const res = await api.get(`/api/v1/subout-notifications/${oid}/edd`, { responseType: 'blob' });
  const disp = String(res.headers?.['content-disposition'] ?? '');
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disp);
  return { blob: res.data as Blob, fileName: m?.[1] ? decodeURIComponent(m[1]) : 'EDD.xlsx' };
}

/** Delete the selected orders (bulk). */
export async function deleteTestOrders(orderOids: string[]): Promise<{ deleted: number; rows: SuboutTestOrderActionResult[] }> {
  const res = await api.post(`${TO}/delete`, orderOids);
  return (res.data as { data: { deleted: number; rows: SuboutTestOrderActionResult[] } }).data;
}

/** Pull the human message out of an axios error, else a fallback. */
export function apiMessage(e: unknown, fallback: string): string {
  const resp = (e as { response?: { data?: { error?: { message?: string } } } })?.response;
  return resp?.data?.error?.message ?? fallback;
}
